import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { api, money } from '../../lib/api';
import { useBase, useScope } from '../../scope';
import { CentsInput, OverrideInput } from './bits';
import { byRank, errMsg, type Modifier, type ModifierRequest } from './types';
import './menu.css';

type OptionDraft = { optionId: string | null; name: string; extraChargeInCents: number; isDefault: boolean };
type Draft = { name: string; minSelect: number; maxSelect: number; options: OptionDraft[] };

const fresh = (): Draft => ({ name: '', minSelect: 0, maxSelect: 1, options: [{ optionId: null, name: '', extraChargeInCents: 0, isDefault: false }] });

/** Modifier groups — the choices an item asks for (cook level, sauce,
 * extras). A group is replaced whole; a known optionId keeps its identity. */
export function Modifiers() {
  const { rid, sid } = useScope();
  const base = useBase();
  const [groups, setGroups] = useState<Modifier[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(fresh());

  const load = useCallback(async () => {
    try {
      setGroups((await api.get<Modifier[]>(`${base}/modifiers`)).sort(byRank));
      setError(null);
    } catch (e) {
      setError(errMsg(e, 'Failed to load modifier groups.'));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      await load();
      setError(null);
    } catch (e) {
      setError(errMsg(e, 'That didn’t work.'));
    } finally {
      setBusy(false);
    }
  }

  const body = (d: Draft): ModifierRequest => ({
    name: d.name.trim(),
    minSelect: d.minSelect,
    maxSelect: d.maxSelect,
    options: d.options.map((o) => ({ optionId: o.optionId, name: o.name.trim(), extraChargeInCents: o.extraChargeInCents, isDefault: o.isDefault })),
  });

  const valid = (d: Draft) => d.name.trim() && d.options.length > 0 && d.options.every((o) => o.name.trim()) && d.minSelect <= d.maxSelect;

  const form = (
    <div className="mn-step">
      <div className="mn-step-head">
        <input className="input" placeholder="Group name, e.g. Cook level" aria-label="Group name" value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <span className="lbl mn-hint">pick</span>
        <OverrideInput value={draft.minSelect} placeholder="min" onChange={(v) => setDraft({ ...draft, minSelect: v ?? 0 })} />
        <span className="lbl mn-hint">–</span>
        <OverrideInput value={draft.maxSelect} placeholder="max" onChange={(v) => setDraft({ ...draft, maxSelect: v ?? 1 })} />
      </div>
      {draft.options.map((o, i) => (
        <div key={i} className="mn-opt-row">
          <input className="input" placeholder="Option name" aria-label="Option name" value={o.name}
            onChange={(e) => setDraft({ ...draft, options: draft.options.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
          <span className="mn-hint" style={{ whiteSpace: 'nowrap' }}>+ extra</span>
          <CentsInput value={o.extraChargeInCents} title="Option extra"
            onChange={(c) => setDraft({ ...draft, options: draft.options.map((x, j) => (j === i ? { ...x, extraChargeInCents: c } : x)) })} />
          <label className="mn-check" title="Pre-selected">
            <input type="checkbox" checked={o.isDefault}
              onChange={(e) => setDraft({ ...draft, options: draft.options.map((x, j) => (j === i ? { ...x, isDefault: e.target.checked } : x)) })} />
            default
          </label>
          <button className="mn-iconbtn danger" title="Remove option" onClick={() => setDraft({ ...draft, options: draft.options.filter((_, j) => j !== i) })}>
            <X size={15} />
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setDraft({ ...draft, options: [...draft.options, { optionId: null, name: '', extraChargeInCents: 0, isDefault: false }] })}>
          <Plus size={14} /> Add option
        </button>
        <span style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}><X size={14} /> Cancel</button>
        <button className="btn btn-primary btn-sm" disabled={busy || !valid(draft)}
          onClick={() => void act(async () => {
            if (editing === 'new') await api.post(`${base}/modifiers`, body(draft));
            else await api.put(`${base}/modifiers/${editing}`, body(draft));
            setEditing(null);
          })}>
          <Check size={14} /> {editing === 'new' ? 'Create group' : 'Save group'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div>
          <div className="overline">Store · Menu · Modifier groups</div>
          <h1>Modifier groups</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Link className="btn btn-outline btn-sm" to={`/r/${rid}/s/${sid}/menu`}>Back to menu</Link>
          <button className="btn btn-primary btn-sm" disabled={busy || editing === 'new'} onClick={() => { setDraft(fresh()); setEditing('new'); }}>
            <Plus size={14} /> New group
          </button>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}
      {groups === null && <div className="loading">Loading…</div>}
      {editing === 'new' && form}
      {groups !== null && (
        <div className="panel">
          {groups.length === 0 && editing !== 'new' && <div className="loading">No modifier groups yet.</div>}
          {groups.map((g) => (
            <div key={g.modifierId} className="rowline" style={{ flexWrap: 'wrap' }}>
              {editing === g.modifierId ? (
                <div style={{ flex: '1 1 100%' }}>{form}</div>
              ) : (
                <>
                  <div className="grow">
                    <b>{g.name}</b>
                    <span className="sub">
                      pick {g.minSelect}–{g.maxSelect} · {g.options.map((o) => `${o.name}${o.extraChargeInCents ? ` +${money(o.extraChargeInCents)}` : ''}${o.isDefault ? ' (default)' : ''}`).join(', ')}
                    </span>
                  </div>
                  <button className="mn-iconbtn" title="Edit group" disabled={busy}
                    onClick={() => { setEditing(g.modifierId); setDraft({ name: g.name, minSelect: g.minSelect, maxSelect: g.maxSelect, options: g.options.map((o) => ({ ...o })) }); }}>
                    <Pencil size={14} />
                  </button>
                  <button className="mn-iconbtn danger" title="Delete group" disabled={busy}
                    onClick={() => { if (confirm(`Delete group “${g.name}”?`)) void act(() => api.del(`${base}/modifiers/${g.modifierId}`)); }}>
                    <Trash2 size={15} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
